import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { Users, Plus, Search, Edit3, X, Award, AlertTriangle, Trash2, QrCode, Printer, Download } from 'lucide-react';
import LoadingSpinner from './components/LoadingSpinner';
import AccreditationsModal from './components/AccreditationsModal';
import { filterStaffByAccess, normalizeAssignedGroupIds } from './utils/locationAccess';
import { QRCodeSVG } from 'qrcode.react';

export default function StaffManager({ user, userData, companyId, companyData }) {
  const userLimit = companyData?.shiftsUserLimit || 0;
  const { t } = useTranslation();
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [accredStaff, setAccredStaff] = useState(null);
  const [qrStaff, setQrStaff] = useState(null);
  const [allAccreditations, setAllAccreditations] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!companyId) return;

    const q = query(collection(db, 'companies', companyId, 'users'));
    const unsub = onSnapshot(q, (snapshot) => {
      setStaffList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Error listening to staff:', error);
      setLoading(false);
    });

    return unsub;
  }, [companyId]);

  // Load all accreditations for badge counts
  useEffect(() => {
    if (!companyId) return;
    const q2 = query(collection(db, 'companies', companyId, 'staffAccreditations'));
    const unsub2 = onSnapshot(q2, (snap) => {
      setAllAccreditations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('Error loading accreditations:', err));
    return unsub2;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(
      collection(db, 'companies', companyId, 'shiftsGroups'),
      (snapshot) => setGroups(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('Error loading groups:', err)
    );
    return unsub;
  }, [companyId]);

  // Helper: get accreditation summary for a staff member
  const getAccredSummary = (userId) => {
    const items = allAccreditations.filter(a => a.userId === userId);
    const now = new Date();
    let expired = 0, expiring = 0, valid = 0;
    items.forEach(a => {
      if (!a.expiryDate) { valid++; return; }
      const days = Math.ceil((new Date(a.expiryDate) - now) / (1000 * 60 * 60 * 24));
      if (days < 0) expired++;
      else if (days <= 30) expiring++;
      else valid++;
    });
    return { total: items.length, expired, expiring, valid };
  };

  const visibleStaff = filterStaffByAccess(staffList, userData, user?.uid);

  const filteredStaff = visibleStaff.filter(s => {
    const name = (s.displayName || s.username || '').toLowerCase();
    const email = (s.email || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase()) || email.includes(searchQuery.toLowerCase());
  });

  const groupsById = groups.reduce((acc, group) => {
    acc[group.id] = group;
    return acc;
  }, {});

  const uploadProfilePhoto = async (userId, file, existingPath = null) => {
    if (!file || !userId) return { photoURL: null, photoPath: existingPath || null };

    const safeFileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const photoPath = `companies/${companyId}/staffProfiles/${userId}/${safeFileName}`;
    const storageRef = ref(storage, photoPath);

    await uploadBytes(storageRef, file);
    const photoURL = await getDownloadURL(storageRef);

    if (existingPath && existingPath !== photoPath) {
      try {
        await deleteObject(ref(storage, existingPath));
      } catch (_) {}
    }

    return { photoURL, photoPath };
  };

  const saveStaffProfile = async (profileData) => {
    try {
      if (editingStaff) {
        const nextPhoto = profileData.photoFile
          ? await uploadProfilePhoto(editingStaff.id, profileData.photoFile, editingStaff.photoPath)
          : profileData.removePhoto
            ? { photoURL: null, photoPath: null }
            : { photoURL: editingStaff.photoURL || null, photoPath: editingStaff.photoPath || null };

        if (profileData.removePhoto && editingStaff.photoPath) {
          try {
            await deleteObject(ref(storage, editingStaff.photoPath));
          } catch (_) {}
        }

        const updatePayload = {
          ...profileData,
          photoURL: nextPhoto.photoURL,
          photoPath: nextPhoto.photoPath,
          updatedAt: new Date().toISOString(),
        };

        delete updatePayload.photoFile;
        delete updatePayload.removePhoto;

        await updateDoc(doc(db, 'companies', companyId, 'users', editingStaff.id), updatePayload);
        await setDoc(doc(db, 'users', editingStaff.id), {
          displayName: updatePayload.displayName,
          email: updatePayload.email || null,
          username: updatePayload.username || null,
          photoURL: updatePayload.photoURL,
          photoPath: updatePayload.photoPath,
          shiftsAssignedGroupIds: updatePayload.assignedGroupIds || [],
          updatedAt: new Date().toISOString(),
        }, { merge: true });
      } else {
        await addDoc(collection(db, 'companies', companyId, 'users'), {
          ...profileData,
          companyId,
          active: true,
          assignedGroupIds: profileData.assignedGroupIds || [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      setShowAddModal(false);
      setEditingStaff(null);
    } catch (error) {
      console.error('Error saving staff profile:', error);
      alert('Failed to save staff profile');
    }
  };

  const handleDeleteStaff = async (staffMember) => {
    if (!staffMember?.id || !companyId) return;
    if (staffMember.id === user?.uid) {
      window.alert(t('staff.cannotDeleteSelf', 'You cannot remove your own staff account from here.'));
      return;
    }
    if (!window.confirm(
      t(
        'staff.confirmDelete',
        'Remove {{name}} from this company? This deletes their staff profile from this company workspace.',
        { name: staffMember.displayName || staffMember.email || t('common.unnamed', 'Unnamed') }
      )
    )) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'companies', companyId, 'users', staffMember.id));
    } catch (error) {
      console.error('Error deleting staff profile:', error);
      window.alert(t('staff.deleteFailed', 'Failed to delete staff profile'));
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users size={24} className="text-primary" />
              {t('staff.title')}
            </h1>
              <p className="text-gray-400 mt-1">
                {userLimit > 0
                ? t('staff.staffCount', '{{count}} / {{limit}} staff', { count: visibleStaff.length, limit: userLimit })
                : `${visibleStaff.length} ${t('staff.staffMembers')}`}
            </p>
          </div>
          <button
            onClick={() => { setEditingStaff(null); setShowAddModal(true); }}
            disabled={userLimit > 0 && staffList.length >= userLimit}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              userLimit > 0 && staffList.length >= userLimit
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            <Plus size={16} />
            {userLimit > 0 && staffList.length >= userLimit
              ? t('staff.limitReached', 'Limit Reached')
              : t('staff.addNew')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('common.search')}
          className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      </div>

      {/* Staff list */}
      {filteredStaff.length === 0 ? (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
          <Users size={48} className="text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">{t('staff.noStaffFound')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStaff.map(member => (
            <div key={member.id} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <StaffAvatar member={member} />
                  <div>
                    <p className="font-semibold text-white">{member.displayName || member.username}</p>
                    <p className="text-sm text-gray-400">{member.email}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  member.active !== false ? 'bg-green-100 text-green-700' : 'bg-white/10 text-gray-400'
                }`}>
                  {member.active !== false ? t('staff.active') : t('staff.inactive')}
                </span>
              </div>

              <div className="mt-4 space-y-1.5 text-sm text-gray-400">
                {member.role && <p><span className="font-medium">{t('staff.roleLabel')}</span> {member.role}</p>}
                {member.roles && member.roles.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {member.roles.map((r, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{r}</span>
                    ))}
                  </div>
                )}
                {member.skills && member.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {member.skills.map((s, i) => (
                      <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">{s}</span>
                    ))}
                  </div>
                )}
                {member.contractHours && <p><span className="font-medium">{t('staff.contractLabel')}</span> {member.contractHours}{t('staff.hPerWeek')}</p>}
                {member.payrollId && <p><span className="font-medium">{t('staff.payrollId', 'Payroll / Staff ID')}</span> {member.payrollId}</p>}
                {normalizeAssignedGroupIds(member).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {normalizeAssignedGroupIds(member).map((groupId) => (
                      <span
                        key={groupId}
                        className="px-2 py-0.5 bg-emerald-500/10 text-emerald-300 rounded text-xs"
                      >
                        {groupsById[groupId]?.name || t('staff.unknownLocation', 'Unknown location')}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Accreditation badges */}
              {(() => {
                const summary = getAccredSummary(member.id);
                if (summary.total === 0) return null;
                return (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Award size={13} className="text-primary" />
                      {summary.total} {t('accreditations.certificates')}
                    </span>
                    {summary.expired > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                        <AlertTriangle size={11} /> {summary.expired} {t('accreditations.expired')}
                      </span>
                    )}
                    {summary.expiring > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                        {summary.expiring} {t('accreditations.expiringSoon')}
                      </span>
                    )}
                  </div>
                );
              })()}

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => { setEditingStaff(member); setShowAddModal(true); }}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-primary border border-primary/30 rounded-lg hover:bg-primary/5"
                >
                  <Edit3 size={14} /> {t('common.edit')}
                </button>
                <button
                  onClick={() => setAccredStaff(member)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/5"
                >
                  <Award size={14} /> {t('accreditations.title')}
                </button>
                <button
                  onClick={() => setQrStaff(member)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-sky-300 border border-sky-500/30 rounded-lg hover:bg-sky-500/5"
                >
                  <QrCode size={14} /> {t('staff.qrCard', 'QR Card')}
                </button>
                {member.id !== user?.uid && (
                  <button
                    onClick={() => handleDeleteStaff(member)}
                    className="px-3 py-1.5 text-sm text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/5"
                    title={t('staff.deleteStaff', 'Delete Staff')}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Staff Modal */}
      {showAddModal && (
        <StaffModal
          staff={editingStaff}
          companyId={companyId}
          groups={groups}
          staffList={staffList}
          onClose={() => { setShowAddModal(false); setEditingStaff(null); }}
          onSave={saveStaffProfile}
        />
      )}

      {/* Accreditations Modal */}
      {accredStaff && (
        <AccreditationsModal
          staff={accredStaff}
          companyId={companyId}
          onClose={() => setAccredStaff(null)}
        />
      )}

      {qrStaff && (
        <StaffQRCodeModal
          staff={qrStaff}
          onClose={() => setQrStaff(null)}
        />
      )}
    </div>
  );
}

function StaffQRCodeModal({ staff, onClose }) {
  const { t } = useTranslation();
  const qrRef = useRef(null);
  const staffLabel = staff.displayName || staff.fullName || staff.name || staff.username || staff.email || staff.id;
  const qrValue = `${window.location.origin}/timeclock?staff=${encodeURIComponent(staff.id)}`;
  const clockingCardTitle = t('staff.clockingCardTitle', 'Clocking Card');
  const scanCardLabel = t('staff.scanCardLabel', 'Scan this card at the clocking terminal');

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const svgEl = qrRef.current?.querySelector('svg');
    const svgData = svgEl ? new XMLSerializer().serializeToString(svgEl) : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${staffLabel} - QR Card</title>
        <style>
          body { font-family: Arial, sans-serif; background:#0f172a; display:flex; align-items:center; justify-content:center; min-height:100vh; margin:0; }
          .card { width:420px; background:#ffffff; border-radius:24px; padding:28px; box-sizing:border-box; }
          .brand { display:flex; align-items:center; gap:12px; margin-bottom:20px; }
          .brand img { width:52px; height:52px; object-fit:contain; }
          .photo { width:88px; height:88px; border-radius:999px; object-fit:cover; background:#e2e8f0; }
          .photo-fallback { width:88px; height:88px; border-radius:999px; background:#1d4ed8; color:#fff; display:flex; align-items:center; justify-content:center; font-size:32px; font-weight:700; }
          .header { display:flex; justify-content:space-between; align-items:center; gap:20px; }
          .meta { margin:18px 0 20px; }
          .meta h1 { font-size:28px; margin:0 0 8px; color:#0f172a; }
          .meta p { margin:0; color:#475569; font-size:14px; }
          .qr-wrap { text-align:center; margin-top:24px; }
          .qr { display:inline-block; background:#fff; padding:16px; border-radius:20px; }
          .footer { margin-top:18px; font-size:12px; color:#64748b; text-align:center; }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="brand">
            <img src="${window.location.origin}/logo.png" alt="BuildMyShifts" />
            <div>
              <strong style="font-size:18px;color:#0f172a;">BuildMyShifts</strong>
              <div style="color:#64748b;font-size:13px;">${clockingCardTitle}</div>
            </div>
          </div>
          <div class="header">
            <div class="meta">
              <h1>${staffLabel}</h1>
              <p>${staff.payrollId ? `Payroll / Staff ID: ${staff.payrollId}` : scanCardLabel}</p>
            </div>
            ${staff.photoURL
              ? `<img class="photo" src="${staff.photoURL}" alt="${staffLabel}" />`
              : `<div class="photo-fallback">${staffLabel.charAt(0).toUpperCase()}</div>`}
          </div>
          <div class="qr-wrap">
            <div class="qr">${svgData}</div>
          </div>
          <div class="footer">${scanCardLabel}</div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const handleDownload = () => {
    const svgEl = qrRef.current?.querySelector('svg');
    if (!svgEl) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const img = new Image();

    img.onload = () => {
      canvas.width = 440;
      canvas.height = 440;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 20, 20, 400, 400);
      const link = document.createElement('a');
      link.download = `${staffLabel.replace(/\s+/g, '-')}-clock-card.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-white/10 rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <QrCode size={20} className="text-primary" />
            {t('staff.qrCard', 'QR Card')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded text-gray-300">
            <X size={20} />
          </button>
        </div>

        <div className="rounded-2xl bg-white text-slate-900 p-6">
          <div className="flex items-center gap-3 mb-5">
            <img src="/logo.png" alt="BuildMyShifts" className="w-12 h-12 object-contain" />
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">BuildMyShifts</p>
              <p className="font-semibold text-lg">{clockingCardTitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-5">
            {staff.photoURL ? (
              <img src={staff.photoURL} alt={staffLabel} className="w-20 h-20 rounded-full object-cover border border-slate-200" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-bold">
                {staffLabel.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="text-2xl font-bold">{staffLabel}</p>
              <p className="text-sm text-slate-500">
                {staff.payrollId ? `${t('staff.payrollId', 'Payroll / Staff ID')}: ${staff.payrollId}` : t('staff.scanCardLabel', 'Scan this card at the clocking terminal')}
              </p>
            </div>
          </div>

          <div ref={qrRef} className="bg-white rounded-xl p-4 inline-block border border-slate-200">
            <QRCodeSVG value={qrValue} size={220} level="H" includeMargin={false} />
          </div>
        </div>

        <div className="flex gap-3 pt-5">
          <button
            onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-1.5 bg-primary text-white rounded-lg py-2.5 font-medium hover:bg-primary-dark transition-colors text-sm"
          >
            <Printer size={16} /> {t('groups.printQR')}
          </button>
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-1.5 bg-white/10 text-gray-300 rounded-lg py-2.5 font-medium hover:bg-white/20 transition-colors text-sm"
          >
            <Download size={16} /> {t('groups.downloadQR')}
          </button>
        </div>
      </div>
    </div>
  );
}

function StaffModal({ staff, onClose, onSave, companyId, groups = [], staffList = [] }) {
  const { t } = useTranslation();
  const isEdit = !!staff;
  const [loginOption, setLoginOption] = useState(staff?.loginOption || 'email');
  const [formData, setFormData] = useState({
    displayName: staff?.displayName || '',
    email: staff?.email || '',
    username: staff?.username || '',
    password: '',
    role: staff?.role || 'staff',
    roles: staff?.roles?.join(', ') || '',
    skills: staff?.skills?.join(', ') || '',
    contractHours: staff?.contractHours || '',
    payRate: staff?.payRate || '',
    payrollId: staff?.payrollId || '',
    clockPin: staff?.clockPin || '',
    assignedGroupIds: normalizeAssignedGroupIds(staff),
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(staff?.photoURL || '');
  const [removePhoto, setRemovePhoto] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!photoFile) return undefined;
    const objectUrl = URL.createObjectURL(photoFile);
    setPhotoPreview(objectUrl);
    setRemovePhoto(false);

    return () => URL.revokeObjectURL(objectUrl);
  }, [photoFile]);

  const uploadProfilePhoto = async (userId, file, existingPath = null) => {
    if (!file || !userId) return { photoURL: null, photoPath: existingPath || null };

    const safeFileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const photoPath = `companies/${companyId}/staffProfiles/${userId}/${safeFileName}`;
    const storageRef = ref(storage, photoPath);

    await uploadBytes(storageRef, file);
    const photoURL = await getDownloadURL(storageRef);

    if (existingPath && existingPath !== photoPath) {
      try {
        await deleteObject(ref(storage, existingPath));
      } catch (_) {}
    }

    return { photoURL, photoPath };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isEdit) {
      const normalizedClockPin = formData.clockPin.trim() || null;
      if (normalizedClockPin && normalizedClockPin.length < 4) {
        setError(t('staff.clockPinMin', 'Staff PIN must be at least 4 characters'));
        return;
      }
      const duplicatePin = normalizedClockPin
        ? staffList.find((member) => member.clockPin === normalizedClockPin && member.id !== staff?.id)
        : null;
      if (duplicatePin) {
        setError(t('staff.clockPinInUse', 'That staff PIN is already assigned to another user'));
        return;
      }

      // Editing: just update the Firestore doc
      onSave({
        displayName: formData.displayName,
        email: formData.email || null,
        username: formData.username || null,
        role: formData.role,
        loginOption,
        roles: formData.roles ? formData.roles.split(',').map(r => r.trim()).filter(Boolean) : [],
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        contractHours: formData.contractHours ? Number(formData.contractHours) : null,
        payRate: formData.payRate ? Number(formData.payRate) : null,
        payrollId: formData.payrollId.trim() || null,
        clockPin: normalizedClockPin,
        assignedGroupIds: formData.assignedGroupIds,
        photoFile,
        removePhoto,
      });
      return;
    }

    // Creating: validate then call cloud function
    if ((loginOption === 'email' || loginOption === 'both') && !formData.email.trim()) {
      setError(t('staff.errorEmailRequired'));
      return;
    }
    if ((loginOption === 'username' || loginOption === 'both') && !formData.username.trim()) {
      setError(t('staff.errorUsernameRequired'));
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setError(t('staff.errorPasswordMin'));
      return;
    }

    const normalizedClockPin = formData.clockPin.trim() || null;
    if (normalizedClockPin && normalizedClockPin.length < 4) {
      setError(t('staff.clockPinMin', 'Staff PIN must be at least 4 characters'));
      return;
    }
    const duplicatePin = normalizedClockPin
      ? staffList.find((member) => member.clockPin === normalizedClockPin && member.id !== staff?.id)
      : null;
    if (duplicatePin) {
      setError(t('staff.clockPinInUse', 'That staff PIN is already assigned to another user'));
      return;
    }

    setCreating(true);
    try {
      const functions = getFunctions(undefined, 'europe-west2');
      const createUser = httpsCallable(functions, 'shifts_createUser');
      const result = await createUser({
        email: formData.email || null,
        username: formData.username || null,
        password: formData.password,
        displayName: formData.displayName || formData.email?.split('@')[0] || formData.username || 'Staff',
        role: formData.role,
        companyId,
        loginOption,
        roles: formData.roles ? formData.roles.split(',').map(r => r.trim()).filter(Boolean) : [],
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        contractHours: formData.contractHours ? Number(formData.contractHours) : null,
        payRate: formData.payRate ? Number(formData.payRate) : null,
        payrollId: formData.payrollId.trim() || null,
        clockPin: normalizedClockPin,
        assignedGroupIds: formData.assignedGroupIds,
      });

      if (result.data.success) {
        if (photoFile && result.data.userId) {
          const nextPhoto = await uploadProfilePhoto(result.data.userId, photoFile);
          await updateDoc(doc(db, 'companies', companyId, 'users', result.data.userId), {
            photoURL: nextPhoto.photoURL,
            photoPath: nextPhoto.photoPath,
            updatedAt: new Date().toISOString(),
          });
          await setDoc(doc(db, 'users', result.data.userId), {
            photoURL: nextPhoto.photoURL,
            photoPath: nextPhoto.photoPath,
            updatedAt: new Date().toISOString(),
          }, { merge: true });
        }
        onClose();
      } else {
        throw new Error(result.data.error || 'Failed to create user');
      }
    } catch (err) {
      console.error('Error creating user:', err);
      if (err.message?.includes('email-already-in-use')) setError(t('staff.errorEmailInUse'));
      else if (err.message?.includes('username-already-in-use')) setError(t('staff.errorUsernameInUse'));
      else if (err.message?.includes('weak-password')) setError(t('staff.errorPasswordMin'));
      else if (err.message?.includes('invalid-email')) setError(t('staff.errorInvalidEmail'));
      else setError(err.message || t('staff.errorCreateFailed'));
    } finally {
      setCreating(false);
    }
  };

  const toggleAssignedGroup = (groupId) => {
    setFormData((prev) => ({
      ...prev,
      assignedGroupIds: prev.assignedGroupIds.includes(groupId)
        ? prev.assignedGroupIds.filter((id) => id !== groupId)
        : [...prev.assignedGroupIds, groupId],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-white/10 rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">{isEdit ? t('staff.editStaff') : t('staff.addNew')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded text-gray-300" disabled={creating}><X size={20} /></button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('staff.profilePhoto', 'Profile Photo')}</label>
            <div className="flex items-center gap-4">
              <StaffAvatar member={{ displayName: formData.displayName, username: formData.username, photoURL: removePhoto ? '' : photoPreview }} sizeClassName="w-16 h-16" textClassName="text-xl" />
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-primary/15 file:text-primary hover:file:bg-primary/25"
                  disabled={creating}
                />
                <div className="flex items-center gap-2">
                  {(photoPreview || staff?.photoURL) && (
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoFile(null);
                        setPhotoPreview('');
                        setRemovePhoto(true);
                      }}
                      disabled={creating}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      {t('staff.removePhoto', 'Remove photo')}
                    </button>
                  )}
                  <span className="text-xs text-gray-500">{t('staff.photoHint', 'JPG or PNG, shown across staff views.')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Login Method — only for new users */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('staff.loginMethod')} <span className="text-red-400">*</span></label>
              <div className="space-y-2">
                {[
                  { value: 'email', label: t('staff.loginEmail') },
                  { value: 'username', label: t('staff.loginUsername') },
                  { value: 'both', label: t('staff.loginBoth') },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="radio"
                      name="loginOption"
                      value={opt.value}
                      checked={loginOption === opt.value}
                      onChange={e => setLoginOption(e.target.value)}
                      disabled={creating}
                      className="w-4 h-4 text-primary"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Email */}
          {(isEdit || loginOption === 'email' || loginOption === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('staff.email')} {!isEdit && <span className="text-red-400">*</span>}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
                placeholder="user@example.com"
                required={!isEdit && (loginOption === 'email' || loginOption === 'both')}
                disabled={creating || (isEdit && staff?.email)}
              />
            </div>
          )}

          {/* Username */}
          {(isEdit || loginOption === 'username' || loginOption === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('staff.username')} {!isEdit && <span className="text-red-400">*</span>}
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={e => setFormData({ ...formData, username: e.target.value.replace(/\s+/g, '') })}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
                placeholder="johndoe"
                required={!isEdit && (loginOption === 'username' || loginOption === 'both')}
                disabled={creating || (isEdit && staff?.username)}
              />
              <p className="text-xs text-gray-500 mt-1">{t('staff.usernameHint')}</p>
            </div>
          )}

          {/* Password — only for new users */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('staff.password')} <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
                placeholder={t('staff.passwordPlaceholder')}
                minLength={6}
                required
                disabled={creating}
              />
              <p className="text-xs text-gray-500 mt-1">{t('staff.passwordHint')}</p>
            </div>
          )}

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.name')}</label>
            <input type="text" value={formData.displayName} onChange={e => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white" required disabled={creating} />
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.role')}</label>
            <select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white [&>option]:text-black [&>option]:bg-white"
              disabled={creating}>
              <option value="staff">{t('staff.roleStaff')}</option>
              <option value="manager">{t('staff.roleManager')}</option>
              <option value="admin">{t('staff.roleAdmin')}</option>
              <option value="viewer">{t('staff.roleViewer')}</option>
            </select>
          </div>

          {/* Job Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.jobRoles')}</label>
            <input type="text" value={formData.roles} onChange={e => setFormData({ ...formData, roles: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white" placeholder="Barista, Server, Cashier"
              disabled={creating} />
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.skills')} ({t('staff.commaSeparated')})</label>
            <input type="text" value={formData.skills} onChange={e => setFormData({ ...formData, skills: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white" placeholder="First Aid, Food Hygiene"
              disabled={creating} />
          </div>

          {/* Contract Hours / Pay Rate */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.contractHours')}</label>
              <input type="number" value={formData.contractHours} onChange={e => setFormData({ ...formData, contractHours: e.target.value })}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white" placeholder="40" disabled={creating} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.payRate')}</label>
              <input type="number" step="0.01" value={formData.payRate} onChange={e => setFormData({ ...formData, payRate: e.target.value })}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white" placeholder="12.50" disabled={creating} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.payrollId', 'Payroll / Staff ID')}</label>
            <input
              type="text"
              value={formData.payrollId}
              onChange={e => setFormData({ ...formData, payrollId: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
              placeholder={t('staff.payrollIdPlaceholder', 'e.g. EMP-10024')}
              disabled={creating}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('staff.clockPin', 'Staff PIN')}</label>
            <input
              type="password"
              value={formData.clockPin}
              onChange={e => setFormData({ ...formData, clockPin: e.target.value.replace(/\s+/g, '') })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
              placeholder={t('staff.clockPinPlaceholder', '4+ digits or characters')}
              disabled={creating}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('staff.clockPinHelp', 'Optional PIN staff can enter at a clocking terminal instead of scanning a QR card.')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('staff.assignedLocations', 'Assigned Locations')}</label>
            <div className="max-h-32 overflow-y-auto rounded-lg border border-white/10 bg-white/5 p-2 space-y-2">
              {groups.length === 0 ? (
                <p className="text-sm text-gray-400">{t('staff.noLocationsAvailable', 'Create locations first in Groups & Locations')}</p>
              ) : (
                groups.map((group) => {
                  const selected = formData.assignedGroupIds.includes(group.id);
                  return (
                    <label key={group.id} className="flex items-center justify-between gap-3 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-white/5">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: group.color || '#2563EB' }}
                        />
                        <span className="text-sm text-gray-200">{group.name}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleAssignedGroup(group.id)}
                        disabled={creating}
                        className="h-4 w-4 rounded border-white/20 bg-slate-900 text-primary"
                      />
                    </label>
                  );
                })
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t('staff.assignedLocationsHelp', 'When locations are selected, this user will only see staff and rota data for those locations. Leave empty for full company access.')}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={creating}
              className="flex-1 bg-primary text-white rounded-lg py-2 font-medium hover:bg-primary-dark transition-colors disabled:opacity-50">
              {creating ? t('staff.creating') : t('common.save')}
            </button>
            <button type="button" onClick={onClose} disabled={creating}
              className="flex-1 bg-white/10 text-gray-300 rounded-lg py-2 font-medium hover:bg-white/20 transition-colors">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StaffAvatar({ member, sizeClassName = 'w-10 h-10', textClassName = 'text-lg' }) {
  const initial = (member?.displayName || member?.username || '?').trim().charAt(0).toUpperCase();

  if (member?.photoURL) {
    return (
      <img
        src={member.photoURL}
        alt={member.displayName || member.username || 'Staff'}
        className={`${sizeClassName} rounded-full object-cover border border-white/10 bg-white/5`}
      />
    );
  }

  return (
    <div className={`${sizeClassName} bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold ${textClassName}`}>
      {initial}
    </div>
  );
}
